import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell, ShoppingCart, Users, Package, RefreshCw, Settings,
  CheckCheck, Trash2, Loader2, BellOff, Filter, RotateCcw, AlertTriangle, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Pagination } from '../../components/admin/Pagination';
import { useAdminNotifications, type AdminNotifType, type AdminNotification, type AdminNotificationsResp } from '../../context/AdminNotificationsContext';

// ── Configurazione tipi ──────────────────────────────────────────────────────

const TYPE_CONFIG: Record<AdminNotifType, {
  label: string;
  Icon: React.ElementType;
  colorClass: string;
}> = {
  ordine:   { label: 'Ordine',   Icon: ShoppingCart, colorClass: 'text-blue-600 bg-blue-50' },
  cliente:  { label: 'Cliente',  Icon: Users,        colorClass: 'text-violet-600 bg-violet-50' },
  rma:      { label: 'Reso',     Icon: RotateCcw,    colorClass: 'text-orange-600 bg-orange-50' },
  stock:    { label: 'Stock',    Icon: Package,      colorClass: 'text-cyan-600 bg-cyan-50' },
  erp:      { label: 'ERP',      Icon: RefreshCw,    colorClass: 'text-amber-600 bg-amber-50' },
  sistema:  { label: 'Sistema',  Icon: Settings,     colorClass: 'text-gray-500 bg-gray-100' },
  difformita: { label: 'Difformità', Icon: AlertTriangle, colorClass: 'text-rose-600 bg-rose-50' },
  contatto: { label: 'Contatti', Icon: MessageSquare, colorClass: 'text-teal-600 bg-teal-50' },
};

const FILTERS: { label: string; value: string }[] = [
  { label: 'Tutte',       value: '' },
  { label: 'Non lette',   value: 'unread' },
  { label: 'Ordini',      value: 'ordine' },
  { label: 'Clienti',     value: 'cliente' },
  { label: 'Resi',        value: 'rma' },
  { label: 'Difformità',  value: 'difformita' },
  { label: 'Contatti',    value: 'contatto' },
  { label: 'Stock',       value: 'stock' },
  { label: 'ERP',         value: 'erp' },
  { label: 'Sistema',     value: 'sistema' },
];

const PAGE_SIZE = 15;

// ── Utils ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = diffMs / 3_600_000;
  if (diffH < 1) return 'Poco fa';
  if (diffH < 24) return `${Math.floor(diffH)}h fa`;
  if (diffH < 48) return 'Ieri';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

// ── Componente ───────────────────────────────────────────────────────────────

export default function AdminNotificationsPage() {
  // Il context resta la fonte per markRead/markAllRead/remove (endpoint condivisi
  // con il badge sidebar) — la lista paginata di questa pagina è gestita localmente
  // (il context globale carica sempre le ultime 100, senza filtri server-side).
  const { markRead, markAllRead, remove, reload: reloadSidebarBadge } = useAdminNotifications();

  const [activeFilter, setActiveFilter] = useState('unread');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const requestIdRef = useRef(0);

  const load = () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String((page - 1) * PAGE_SIZE));
    if (activeFilter === 'unread') params.set('unread_only', 'true');
    else if (activeFilter) params.set('type', activeFilter);

    api.get<AdminNotificationsResp>(`/api/admin/notifications?${params.toString()}`)
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) return;
        setItems(data.items);
        setTotal(data.total);
        setUnreadCount(data.unreadCount);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setItems([]);
        setTotal(0);
      })
      .finally(() => { if (requestId === requestIdRef.current) setLoading(false); });
  };

  useEffect(() => { setPage(1); }, [activeFilter]);
  useEffect(() => { load(); }, [activeFilter, page]);

  const handleMarkRead = async (n: AdminNotification) => {
    if (n.read) return;
    setPendingId(n.id);
    try {
      await markRead(n.id);
      load();
      reloadSidebarBadge();
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setPendingId(id);
    try {
      await remove(id);
      toast.success('Notifica eliminata');
      // Se era l'unico elemento della pagina corrente (e non siamo a pag.1), torna indietro
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
      reloadSidebarBadge();
    } catch {
      toast.error('Errore eliminazione');
    } finally {
      setPendingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      toast.success('Tutte le notifiche segnate come lette');
      load();
      reloadSidebarBadge();
    } catch {
      toast.error('Errore');
    }
  };

  return (
    <div className="p-6" data-testid="admin-notifications-page">
      {/* Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifiche di Sistema</h1>
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="admin-notif-counter">
            {loading
              ? 'Caricamento…'
              : `${total} risultati · ${unreadCount} non ${unreadCount === 1 ? 'letta' : 'lette'} in totale`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 bg-white hover:bg-secondary/50 transition-colors"
              data-testid="admin-notif-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Segna tutto letto
            </button>
          )}
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 bg-white hover:bg-secondary/50 transition-colors"
            data-testid="admin-notif-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Filter pills ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 mb-5 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setActiveFilter(f.value)}
            data-testid={`admin-notif-filter-${f.value || 'all'}`}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeFilter === f.value
                ? 'bg-[hsl(214,58%,41%)] text-white'
                : 'border bg-white text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            {f.label}
            {f.value === 'unread' && unreadCount > 0 && (
              <span className="ml-1.5 text-[9px] bg-rose-500 text-white rounded-full px-1.5 py-0.5">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lista ───────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border bg-white p-14 flex flex-col items-center text-center gap-3"
          data-testid="admin-notif-empty"
        >
          <BellOff className="w-10 h-10 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">
            {activeFilter ? 'Nessuna notifica in questa categoria' : 'Nessuna notifica di sistema'}
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-xs">
            Le notifiche vengono generate automaticamente da eventi del sistema: nuovi ordini, clienti registrati, alert stock, sync ERP.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4" data-testid="admin-notif-list">
            {items.map(n => {
              const cfg = TYPE_CONFIG[n.type] ?? TYPE_CONFIG.sistema;
              const busy = pendingId === n.id;
              return (
                <div
                  key={n.id}
                  className={`rounded-xl border bg-white p-4 flex gap-3 transition-colors ${
                    !n.read ? 'border-blue-100 bg-blue-50/30' : 'hover:bg-secondary/20'
                  }`}
                  data-testid={`admin-notif-item-${n.id}`}
                >
                  {/* Icona tipo */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.colorClass}`}>
                    <cfg.Icon className="w-4 h-4" />
                  </div>

                  {/* Contenuto */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-sm font-medium truncate ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {n.title}
                        </span>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0">
                        {formatDate(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.body}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {n.actionLink && (
                        <Link
                          to={n.actionLink}
                          className="text-xs text-[hsl(214,58%,41%)] hover:underline"
                          onClick={() => !n.read && handleMarkRead(n)}
                        >
                          Vai →
                        </Link>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cfg.colorClass}`}>
                        {cfg.label}
                      </span>
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {!n.read && (
                      <button
                        onClick={() => handleMarkRead(n)}
                        disabled={busy}
                        title="Segna come letta"
                        className="w-7 h-7 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/50 disabled:opacity-50 transition-colors"
                        data-testid={`admin-notif-read-${n.id}`}
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(n.id)}
                      disabled={busy}
                      title="Elimina"
                      className="w-7 h-7 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
                      data-testid={`admin-notif-delete-${n.id}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="rounded-xl border bg-white overflow-hidden">
            <Pagination page={page} pages={pages} isLoading={loading} onChange={setPage} testIdPrefix="admin-notif" />
          </div>
        </>
      )}
    </div>
  );
}
